// vg-binding: P7.D-18
//
// apps/vendor/src/features/products/add/MethodPicker.tsx
//
// Phase 7 Wave 8 Task 28 — first step of the Add Product wizard.
// Picks the submission method.

import { cn } from '@printway/ui';

import { useTranslation } from '../../../lib/i18n/index.js';
import type { SubmissionMethod } from '../api.js';

interface Card {
  method: SubmissionMethod;
  labelKey: string;
  descriptionKey: string;
  testId: string;
}

const CARDS: readonly Card[] = [
  {
    method: 'catalog',
    labelKey: 'method.catalogLabel',
    descriptionKey: 'method.catalogDescription',
    testId: 'add-product-method-catalog',
  },
  {
    method: 'file',
    labelKey: 'method.fileLabel',
    descriptionKey: 'method.fileDescription',
    testId: 'add-product-method-file',
  },
  {
    method: 'api',
    labelKey: 'method.apiLabel',
    descriptionKey: 'method.apiDescription',
    testId: 'add-product-method-api',
  },
];

//  (Amendment #6) — catalog/api hidden, file-only. BE 3-method
// support stays intact; re-enabling = add the method back here.
const VISIBLE_METHODS: readonly SubmissionMethod[] = ['file'];

const VISIBLE_CARDS: readonly Card[] = CARDS.filter((c) =>
  VISIBLE_METHODS.includes(c.method),
);

interface Props {
  active: SubmissionMethod | null;
  onPick: (m: SubmissionMethod) => void;
}

export function MethodPicker({ active, onPick }: Props): JSX.Element {
  const { t } = useTranslation('products');
  return (
    <section className="grid grid-cols-1 gap-3">
      {VISIBLE_CARDS.map((c) => {
        const isActive = c.method === active;
        return (
          <button
            key={c.method}
            type="button"
            data-testid={c.testId}
            onClick={() => onPick(c.method)}
            className={cn(
              'rounded-xl border p-4 text-left transition-colors',
              isActive
                ? 'border-pw-primary bg-pw-primary/5 ring-2 ring-pw-primary'
                : 'border-pw-border bg-pw-bg hover:bg-pw-surface',
            )}
          >
            <p className="text-sm font-semibold text-pw-text">{t(c.labelKey)}</p>
            <p className="mt-2 text-xs text-pw-text-muted">{t(c.descriptionKey)}</p>
          </button>
        );
      })}
    </section>
  );
}

export default MethodPicker;
